/**
 * Read aloud: turns LaTeX into sentences a speech voice can say, each character mapped back to the
 * source so the editor can highlight the sentence and word being spoken.
 *
 * It's a reader, not a TeX engine. It knows the common commands and environments, says maths as
 * words ("x squared"), reads headings and captions as sentences of their own, says acronyms and
 * user macros from their definitions, and skips what isn't prose: the preamble, labels, tables,
 * TikZ, code listings and parenthetical citations.
 */

export interface Sentence {
  text: string;
  /** The source range the sentence came from. */
  from: number;
  to: number;
  /** The source range of each character of `text`. */
  starts: Int32Array;
  ends: Int32Array;
}

export interface GlossaryEntry {
  /** What \gls says, e.g. "STL". LaTeX, so it can hold maths. */
  short: string;
  long?: string;
  plural?: string;
}

export interface SpeechContext {
  glossary?: Map<string, GlossaryEntry>;
  /** Commands without arguments, name → LaTeX body, e.g. "Rw" → "R_{\mathrm{w}}". */
  macros?: Map<string, string>;
  citations?: Map<string, { author?: string; year?: string }>;
}

// ---------------------------------------------------------------- output

interface Block {
  chars: string[];
  starts: number[];
  ends: number[];
}

/** Collects spoken text in blocks (paragraphs, headings, list items), collapsing whitespace. */
class Out {
  blocks: Block[] = [];
  chars: string[] = [];
  starts: number[] = [];
  ends: number[] = [];
  /** While set, everything pushed maps here: text from a macro or glossary definition rather than the file. */
  fixed: [number, number] | null = null;

  private add(c: string, from: number, to: number) {
    const last = this.chars[this.chars.length - 1];
    if (c === " " || c === "\n" || c === "\t" || c === "\r" || c === " ") {
      if (!this.chars.length || last === " " || last === "(") return;
      c = " ";
    } else if (last === " " && ".,;:!?)".includes(c)) {
      // Something silent (a citation, a label) left a gap before the punctuation.
      this.chars.pop();
      this.starts.pop();
      this.ends.pop();
    }
    this.chars.push(c);
    this.starts.push(from);
    this.ends.push(to);
  }

  /** Text that came from source[from, to). */
  push(s: string, from: number, to: number) {
    if (this.fixed) [from, to] = this.fixed;
    for (let k = 0; k < s.length; k++) this.add(s[k], from, to);
  }

  /** Source characters said as they are, each mapped to itself. */
  source(src: string, from: number, to: number) {
    for (let k = from; k < to; k++) {
      if (this.fixed) this.add(src[k], this.fixed[0], this.fixed[1]);
      else this.add(src[k], k, k + 1);
    }
  }

  /** Punctuation that sticks to the previous word. */
  punct(p: string, from: number, to: number) {
    this.trim();
    const last = this.chars[this.chars.length - 1];
    if (last && !".,;:!?".includes(last)) this.push(p + " ", from, to);
    else if (last) this.push(" ", from, to);
  }

  append(o: Out) {
    for (let k = 0; k < o.chars.length; k++) this.add(o.chars[k], o.starts[k], o.ends[k]);
  }

  words(): number {
    return this.chars.join("").split(" ").filter(Boolean).length;
  }

  private trim() {
    while (this.chars.length && this.chars[this.chars.length - 1] === " ") {
      this.chars.pop();
      this.starts.pop();
      this.ends.pop();
    }
  }

  /** Ends the block: a paragraph break, or around a heading or caption. */
  brk() {
    this.trim();
    if (this.chars.length) this.blocks.push({ chars: this.chars, starts: this.starts, ends: this.ends });
    this.chars = [];
    this.starts = [];
    this.ends = [];
  }
}

// ---------------------------------------------------------------- vocabulary

const SECTIONS = new Set(["part", "chapter", "section", "subsection", "subsubsection", "paragraph", "subparagraph", "addchap", "addsec", "minisec", "frametitle", "title"]);

/** Commands whose last argument is read as text, with how many arguments to skip before it. */
const READ: Record<string, number> = {
  textbf: 0, textit: 0, emph: 0, textsl: 0, textsc: 0, textup: 0, textmd: 0, textrm: 0, textsf: 0, texttt: 0, textnormal: 0,
  underline: 0, uline: 0, uwave: 0, sout: 0, hl: 0, mbox: 0, fbox: 0, makebox: 0, framebox: 0, text: 0, enquote: 0, textquote: 0,
  MakeUppercase: 0, MakeLowercase: 0, MakeTextUppercase: 0, textsuperscript: 0, textsubscript: 0, hyperref: 0, NoCaseChange: 0,
  textcolor: 1, colorbox: 1, foreignlanguage: 1, raisebox: 1, href: 1, parbox: 1, highlight: 0, ul: 0, st: 0, lowercase: 0, uppercase: 0,
};

/** Commands skipped with this many required arguments (optional [..] arguments are always skipped). */
const SKIP: Record<string, number> = {
  label: 1, index: 1, glossary: 1, vspace: 1, hspace: 1, vskip: 0, hskip: 0, includegraphics: 1, input: 1, include: 1, includeonly: 1,
  bibliography: 1, bibliographystyle: 1, addbibresource: 1, printbibliography: 0, usepackage: 1, RequirePackage: 1, documentclass: 1,
  setlength: 2, addtolength: 2, setcounter: 2, addtocounter: 2, stepcounter: 1, refstepcounter: 1, newcounter: 1, pagestyle: 1,
  thispagestyle: 1, pagenumbering: 1, numberwithin: 2, captionsetup: 1, hypersetup: 1, graphicspath: 1, geometry: 1, newgeometry: 1,
  linespread: 1, color: 1, definecolor: 3, tikzset: 1, pgfplotsset: 1, usetikzlibrary: 1, usepgfplotslibrary: 1, tikzexternalize: 0,
  newglossaryentry: 2, newacronym: 3, newabbreviation: 3, DeclareAcronym: 2, addcontentsline: 3, markboth: 2, markright: 1, fontsize: 2,
  rule: 2, resizebox: 3, scalebox: 2, includepdf: 1, todo: 1, missingfigure: 1, nocite: 1, printglossary: 0, setglossarystyle: 1,
  bibitem: 1, cline: 1, multicolumn: 3, multirow: 3, specialrule: 3, cmidrule: 1, arraystretch: 0, tabcolsep: 0, glsadd: 1,
  pgfmathsetmacro: 2, pgfplotstableread: 2, lstinputlisting: 1, inputminted: 2, setminted: 1, lstset: 1, DTLloaddb: 2, sisetup: 1,
  addplot: 0, settowidth: 2, newlength: 1, phantom: 1, hphantom: 1, vphantom: 1, author: 1, date: 1, affil: 1, thanks: 1, url: 1,
  ExecuteBindingsOnce: 1, newtheorem: 2, theoremstyle: 1, crefname: 3, Crefname: 3, newrobustcmd: 2, AtBeginDocument: 1, ifthenelse: 3,
  footnotetext: 1, enlargethispage: 1, renewenvironment: 3, newenvironment: 3, pdfbookmark: 2, bookmark: 1,
};

/** Commands that say nothing and take no arguments. */
const SILENT = new Set(
  ("centering noindent indent newpage clearpage cleardoublepage maketitle tableofcontents listoffigures listoftables appendix frontmatter " +
    "mainmatter backmatter raggedright raggedleft small large Large LARGE huge Huge footnotesize scriptsize tiny normalsize bfseries itshape " +
    "ttfamily rmfamily sffamily scshape upshape mdseries normalfont bf it tt em sc sl rm sf hfill vfill smallskip medskip bigskip par protect " +
    "relax null leavevmode pagebreak nopagebreak hline toprule midrule bottomrule glsresetall glsaddall printglossaries FloatBarrier " +
    "onehalfspacing doublespacing singlespacing sloppy fussy selectfont footnotemark noalign hrule vrule strut mathstrut displaystyle " +
    "textstyle phantomsection makeatletter makeatother printnomenclature printacronyms printindex makeindex makeglossaries " +
    "frenchspacing nonfrenchspacing justifying centerline endinput ignorespaces unskip tableofcontents listoftheorems clearfloatpage " +
    "allowbreak nobreak break hbox vbox newline linebreak space thinspace enspace quad qquad hfil vfil filbreak").split(" "),
);
/** Unknown commands with these names configure things rather than print text, so their arguments aren't read. */
const CONFIG_RE = /^(Declare|Provide|New|Renew|Set|Add|set|new|renew|provide|define|declare|add|pdf|pgf|tikz|fancy|title|rowcolors|pagecolor|[lcr](head|foot)|AtBegin|AtEnd|apptocmd|pretocmd|patchcmd|robustify|hyp)/;
/** Line-break-like commands that should leave a gap between words. */
const SPACERS = new Set(["newline", "linebreak", "space", "thinspace", "enspace", "quad", "qquad", "hfill", "hfil", "vfill", "par"]);

/** Commands said as a word (or character). */
const WORDS: Record<string, string> = {
  LaTeX: "LaTeX", LaTeXe: "LaTeX 2e", TeX: "TeX", BibTeX: "BibTeX", XeLaTeX: "XeLaTeX", LuaLaTeX: "LuaLaTeX", ldots: "…", dots: "…",
  textellipsis: "…", today: "today", S: "section ", P: "paragraph ", textendash: "–", textemdash: "—", textquoteright: "’",
  textquoteleft: "‘", textquotedblleft: "“", textquotedblright: "”", textdegree: " degrees", textpm: " plus or minus ",
  texttimes: " times ", textasciitilde: "~", textbar: "|", copyright: "©", textregistered: "®", texttrademark: "™", pounds: "£",
  euro: "€", textdollar: "$", textpercent: "%", textbullet: "•", i: "i", j: "j", o: "ø", O: "Ø", ae: "æ", AE: "Æ", oe: "œ",
  OE: "Œ", aa: "å", AA: "Å", l: "ł", L: "Ł", ss: "ß", eg: "e.g.", ie: "i.e.", etal: "et al", etc: "etc.", vs: "vs.", cf: "cf.",
  checkmark: "tick", tick: "tick", textless: "<", textgreater: ">", textunderscore: "_", textbackslash: "", degree: " degrees",
  celsius: " degrees Celsius", micro: "micro", ohm: "ohm",
};

/** \c{c}, \v{s} and friends: single-letter commands that put an accent on the next letter. */
const LETTER_ACCENTS: Record<string, string> = { c: "̧", v: "̌", u: "̆", H: "̋", r: "̊", k: "̨", d: "̣", b: "̱" };
const SYMBOL_ACCENTS: Record<string, string> = { "'": "́", "`": "̀", "^": "̂", '"': "̈", "~": "̃", "=": "̄", ".": "̇" };

const MATH_ENVS = new Set(["equation", "align", "alignat", "gather", "multline", "flalign", "eqnarray", "displaymath", "math", "dmath", "dgroup", "IEEEeqnarray"]);
const SKIP_ENVS = new Set(
  ("tabular tabularx tabulary tabu array tikzpicture pgfpicture axis semilogxaxis semilogyaxis loglogaxis polaraxis groupplot circuitikz " +
    "verbatim Verbatim lstlisting minted comment filecontents thebibliography forest pspicture algorithmic algorithmic* algorithm2e " +
    "BVerbatim LVerbatim alltt spverbatim tcblisting pycode luacode sagesilent").split(" "),
);
/** Floats: only their captions are read, after this word. */
const FLOATS: Record<string, string> = {
  figure: "Figure", wrapfigure: "Figure", SCfigure: "Figure", sidewaysfigure: "Figure", subfigure: "Figure", marginfigure: "Figure",
  table: "Table", wraptable: "Table", sidewaystable: "Table", longtable: "Table", subtable: "Table", margintable: "Table",
  listing: "Listing", algorithm: "Algorithm",
};
const LISTS = new Set(["itemize", "enumerate", "description", "list", "compactitem", "compactenum", "inparaenum", "asparaenum", "tasks", "checklist"]);
const THEOREMS: Record<string, string> = {
  theorem: "Theorem", lemma: "Lemma", proposition: "Proposition", corollary: "Corollary", definition: "Definition", remark: "Remark",
  example: "Example", proof: "Proof", note: "Note", assumption: "Assumption", hypothesis: "Hypothesis", conjecture: "Conjecture",
  claim: "Claim", exercise: "Exercise", problem: "Problem", solution: "Solution", observation: "Observation", question: "Question",
};
/** Required arguments to skip at the start of an environment, e.g. minipage's width. */
const ENV_ARGS: Record<string, number> = { minipage: 1, multicols: 1, spacing: 1, adjustwidth: 2, column: 1, wrapfigure: 2, wraptable: 2, subfigure: 1, subtable: 1, otherlanguage: 1, block: 0, landscape: 0, tcolorbox: 0 };

const GREEK = new Set(
  ("alpha beta gamma delta epsilon zeta eta theta iota kappa lambda mu nu xi omicron pi rho sigma tau upsilon phi chi psi omega " +
    "Gamma Delta Theta Lambda Xi Pi Sigma Upsilon Phi Psi Omega").split(" "),
);
const VAR_GREEK: Record<string, string> = { varepsilon: "epsilon", vartheta: "theta", varpi: "pi", varrho: "rho", varsigma: "sigma", varphi: "phi", varkappa: "kappa", ell: "l", hbar: "h bar", imath: "i", jmath: "j" };

/** Maths commands said as words; "" means silent. */
const MATH_WORDS: Record<string, string> = {
  times: "times", cdot: "times", div: "divided by", pm: "plus or minus", mp: "minus or plus", ast: "times", star: "star",
  leq: "less than or equal to", le: "less than or equal to", leqslant: "less than or equal to", geq: "greater than or equal to",
  ge: "greater than or equal to", geqslant: "greater than or equal to", neq: "not equal to", ne: "not equal to", approx: "approximately",
  sim: "approximately", simeq: "approximately", cong: "is congruent to", equiv: "is equivalent to", propto: "proportional to",
  ll: "much less than", gg: "much greater than", to: "to", rightarrow: "to", leftarrow: "from", Rightarrow: "implies", implies: "implies",
  Leftrightarrow: "if and only if", iff: "if and only if", mapsto: "maps to", infty: "infinity", partial: "partial", nabla: "del",
  sum: "the sum of", prod: "the product of", int: "the integral of", iint: "the double integral of", oint: "the contour integral of",
  forall: "for all", exists: "there exists", in: "in", notin: "not in", subset: "subset of", subseteq: "subset of", cup: "union",
  cap: "intersection", emptyset: "the empty set", varnothing: "the empty set", angle: "angle", perp: "perpendicular to", parallel: "parallel to",
  circ: "degrees", degree: "degrees", prime: "prime", ldots: "dot dot dot", cdots: "dot dot dot", dots: "dot dot dot", vdots: "", ddots: "",
  colon: "", mid: "given", vert: "", lvert: "", rvert: "", Vert: "", langle: "", rangle: "", lfloor: "", rfloor: "", lceil: "", rceil: "",
  quad: "", qquad: "", displaystyle: "", textstyle: "", scriptstyle: "", limits: "", nolimits: "", nonumber: "", notag: "", mathstrut: "",
  left: "", right: "", big: "", Big: "", bigg: "", Bigg: "", bigl: "", bigr: "", Bigl: "", Bigr: "", biggl: "", biggr: "", Biggl: "",
  Biggr: "", middle: "", lbrace: "", rbrace: "", lbrack: "", rbrack: "", backslash: "", setminus: "minus", land: "and", lor: "or",
  neg: "not", lnot: "not", wedge: "and", vee: "or", oplus: "plus", otimes: "times", bullet: "", therefore: "therefore", because: "because",
  dagger: "dagger", percent: "percent", Re: "the real part of", Im: "the imaginary part of", aleph: "aleph", uparrow: "up", downarrow: "down",
};
const FUNCS = new Set("sin cos tan sec csc cot sinh cosh tanh arcsin arccos arctan log lg exp max min sup inf lim liminf limsup det arg dim ker deg gcd Pr mod bmod pmod".split(" "));
const DELIMITED = new Set(["left", "right", "big", "Big", "bigg", "Bigg", "bigl", "bigr", "Bigl", "Bigr", "biggl", "biggr", "Biggl", "Biggr", "middle"]);
const MATH_ACCENTS: Record<string, string> = { hat: "hat", widehat: "hat", bar: "bar", overline: "bar", tilde: "tilde", widetilde: "tilde", dot: "dot", ddot: "double dot", underline: "", check: "check", breve: "breve", acute: "", grave: "" };
const MATH_FONTS = new Set(["mathrm", "mathbf", "mathit", "mathsf", "mathtt", "mathcal", "mathbb", "mathfrak", "mathscr", "boldsymbol", "bm", "operatorname", "mathnormal", "symbf", "symrm", "symit", "mathup", "pmb"]);
const MATH_TEXT = new Set(["text", "textrm", "textit", "textbf", "textsf", "texttt", "textnormal", "mbox", "hbox", "textup", "intertext", "shortintertext"]);
const MATH_OPS: Record<string, string> = { "=": "equals", "+": "plus", "-": "minus", "<": "less than", ">": "greater than", "/": "over", "*": "times", "!": "factorial", "|": "", "(": "", ")": "", "[": "", "]": "", ",": ",", ".": ".", ";": ";", ":": "", "?": "", "@": "", '"': "" };
const MATRIX_ENVS = new Set(["matrix", "pmatrix", "bmatrix", "Bmatrix", "vmatrix", "Vmatrix", "smallmatrix", "array"]);

const SIUNITX = new Set(["SI", "qty", "num", "si", "unit", "ang", "SIrange", "qtyrange", "numrange", "SIlist", "qtylist", "numlist", "tablenum", "qtyproduct", "numproduct"]);
const PREFIXES: Record<string, string> = {
  yocto: "yocto", zepto: "zepto", atto: "atto", femto: "femto", pico: "pico", nano: "nano", micro: "micro", milli: "milli", centi: "centi",
  deci: "deci", deca: "deca", deka: "deca", hecto: "hecto", kilo: "kilo", mega: "mega", giga: "giga", tera: "tera", peta: "peta",
};
/** siunitx unit macros: [singular, plural]. */
const UNITS: Record<string, [string, string]> = {
  metre: ["metre", "metres"], meter: ["metre", "metres"], second: ["second", "seconds"], gram: ["gram", "grams"], kilogram: ["kilogram", "kilograms"],
  hertz: ["hertz", "hertz"], newton: ["newton", "newtons"], pascal: ["pascal", "pascals"], decibel: ["decibel", "decibels"], dB: ["decibel", "decibels"],
  joule: ["joule", "joules"], watt: ["watt", "watts"], volt: ["volt", "volts"], ampere: ["amp", "amps"], kelvin: ["kelvin", "kelvin"],
  degreeCelsius: ["degree Celsius", "degrees Celsius"], celsius: ["degree Celsius", "degrees Celsius"], percent: ["percent", "percent"],
  degree: ["degree", "degrees"], radian: ["radian", "radians"], litre: ["litre", "litres"], liter: ["litre", "litres"], mole: ["mole", "moles"],
  ohm: ["ohm", "ohms"], coulomb: ["coulomb", "coulombs"], farad: ["farad", "farads"], henry: ["henry", "henries"], tesla: ["tesla", "teslas"],
  minute: ["minute", "minutes"], hour: ["hour", "hours"], day: ["day", "days"], bar: ["bar", "bar"], tonne: ["tonne", "tonnes"],
  bel: ["bel", "bels"], neper: ["neper", "nepers"], steradian: ["steradian", "steradians"], candela: ["candela", "candelas"], lux: ["lux", "lux"],
  byte: ["byte", "bytes"], bit: ["bit", "bits"], arcminute: ["arcminute", "arcminutes"], arcsecond: ["arcsecond", "arcseconds"],
};
/** Unit symbols written as letters, e.g. \SI{3}{mm} or $1000~mm$: [singular, plural]. */
const UNIT_SYMBOLS: Record<string, [string, string]> = {
  m: ["metre", "metres"], mm: ["millimetre", "millimetres"], cm: ["centimetre", "centimetres"], km: ["kilometre", "kilometres"], um: ["micrometre", "micrometres"],
  s: ["second", "seconds"], ms: ["millisecond", "milliseconds"], Hz: ["hertz", "hertz"], kHz: ["kilohertz", "kilohertz"], MHz: ["megahertz", "megahertz"],
  GHz: ["gigahertz", "gigahertz"], dB: ["decibel", "decibels"], dBA: ["decibel A", "decibels A"], kg: ["kilogram", "kilograms"], g: ["gram", "grams"],
  mg: ["milligram", "milligrams"], N: ["newton", "newtons"], kN: ["kilonewton", "kilonewtons"], Pa: ["pascal", "pascals"], kPa: ["kilopascal", "kilopascals"],
  MPa: ["megapascal", "megapascals"], GPa: ["gigapascal", "gigapascals"], W: ["watt", "watts"], kW: ["kilowatt", "kilowatts"], V: ["volt", "volts"],
  mV: ["millivolt", "millivolts"], A: ["amp", "amps"], mA: ["milliamp", "milliamps"], J: ["joule", "joules"], kJ: ["kilojoule", "kilojoules"],
  K: ["kelvin", "kelvin"], L: ["litre", "litres"], mL: ["millilitre", "millilitres"], rad: ["radian", "radians"], h: ["hour", "hours"],
  min: ["minute", "minutes"], mol: ["mole", "moles"], Nm: ["newton metre", "newton metres"], rpm: ["r p m", "r p m"],
};

const LABEL_TYPES: Record<string, string> = {
  fig: "figure", figure: "figure", tab: "table", tbl: "table", table: "table", eq: "equation", eqn: "equation", equation: "equation",
  sec: "section", section: "section", ssec: "section", subsec: "section", sub: "section", ch: "chapter", chap: "chapter", chapter: "chapter",
  app: "appendix", apx: "appendix", appendix: "appendix", lst: "listing", listing: "listing", alg: "algorithm", thm: "theorem", lem: "lemma",
  def: "definition", prop: "proposition", cor: "corollary", ex: "example", item: "item", part: "part",
};
const TYPED_REFS = new Set(["cref", "Cref", "autoref", "Autoref", "vref", "Vref", "cpageref", "Cpageref", "labelcref"]);
const REFS = new Set(["ref", "eqref", "pageref", "nameref", "vpageref", "subref", "fullref", "zref", ...TYPED_REFS]);
const TEXTUAL_CITE = /^(text|Text)cites?$|^[cC]ite(t|alt|alp)$|^citet\*$/;

const isLetter = (c: string | undefined) => !!c && ((c >= "a" && c <= "z") || (c >= "A" && c <= "Z"));
const isDigit = (c: string | undefined) => !!c && c >= "0" && c <= "9";

/** "fig:wicket_door" → "wicket door"; "ReferenceValues" → "Reference Values". */
export function labelWords(key: string): { type: string | null; words: string } {
  const m = /^([a-zA-Z]{1,9})[:\-.](.+)$/.exec(key);
  const type = m ? (LABEL_TYPES[m[1].toLowerCase()] ?? null) : null;
  const rest = type ? m![2] : key;
  const words = rest
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_\-:.]+/g, " ")
    .trim();
  return { type, words };
}

function joinAnd(parts: string[]): string {
  const p = parts.filter(Boolean);
  return p.length <= 1 ? (p[0] ?? "") : `${p.slice(0, -1).join(", ")} and ${p[p.length - 1]}`;
}

/** BibTeX author list → "Smith", "Smith and Jones" or "Smith et al". */
export function authorWords(field: string): string {
  const names = field.split(/\s+and\s+/i).map((n) => n.trim()).filter(Boolean);
  const surname = (n: string) => {
    const corporate = /^\{.*\}$/.test(n);
    const t = n.replace(/[{}]/g, "").trim();
    if (corporate) return t;
    if (t.includes(",")) return t.split(",")[0].trim();
    const parts = t.split(/\s+/);
    return parts[parts.length - 1];
  };
  if (!names.length) return "";
  if (names.length === 1) return surname(names[0]);
  if (names.length === 2 && names[1].toLowerCase() !== "others") return `${surname(names[0])} and ${surname(names[1])}`;
  return `${surname(names[0])} et al`;
}

// ---------------------------------------------------------------- reader

interface Arg {
  from: number;
  to: number;
  /** Just after the argument. */
  next: number;
}

class Reader {
  /** Hit \end{document}: stop reading. */
  stopped = false;
  /** The float a caption belongs to ("Figure"), said before it. */
  captionLabel: string | null = null;
  /** Inside \mathrm and friends: letters make words rather than one variable each. */
  private groupLetters = false;
  private lastMathWord = "";

  constructor(
    private src: string,
    public out: Out,
    private ctx: SpeechContext,
    private depth = 0,
  ) {}

  // ------------------------------------------------ scanning helpers

  private inComment(at: number): boolean {
    const s = this.src;
    let k = s.lastIndexOf("\n", at - 1) + 1;
    for (; k < at; k++) {
      if (s[k] === "\\") k++;
      else if (s[k] === "%") return true;
    }
    return false;
  }

  /** From a % to the start of what follows, joining lines the way TeX does. */
  private skipComment(i: number, to: number): number {
    const s = this.src;
    let k = s.indexOf("\n", i);
    if (k < 0 || k >= to) return to;
    // A comment eats its newline; a following blank line still ends the paragraph.
    k++;
    let j = k;
    while (j < to && (s[j] === " " || s[j] === "\t" || s[j] === "\r")) j++;
    const lineStart = s.lastIndexOf("\n", i - 1) + 1;
    const wholeLine = !s.slice(lineStart, i).trim();
    if (!wholeLine && s[j] === "\n") {
      this.out.brk();
      return j + 1;
    }
    return wholeLine ? k : j;
  }

  /** Index of the } matching the { at `open`, or `to`. */
  private matchBrace(open: number, to: number): number {
    const s = this.src;
    let depth = 0;
    for (let k = open; k < to; k++) {
      const c = s[k];
      if (c === "\\") k++;
      else if (c === "%") {
        const n = s.indexOf("\n", k);
        if (n < 0) return to;
        k = n;
      } else if (c === "{") depth++;
      else if (c === "}" && --depth === 0) return k;
    }
    return to;
  }

  /** Spaces and at most one newline (a blank line ends any argument list). */
  private skipSpaces(k: number, to: number): number {
    const s = this.src;
    let newline = false;
    while (k < to) {
      const c = s[k];
      if (c === " " || c === "\t" || c === "\r") k++;
      else if (c === "\n" && !newline) {
        newline = true;
        k++;
      } else break;
    }
    return k;
  }

  private optArg(k: number, to: number): Arg | null {
    const s = this.src;
    const j = this.skipSpaces(k, to);
    if (s[j] !== "[") return null;
    let depth = 0;
    for (let e = j + 1; e < to; e++) {
      const c = s[e];
      if (c === "\\") e++;
      else if (c === "{") depth++;
      else if (c === "}") depth--;
      else if (c === "]" && depth <= 0) return { from: j + 1, to: e, next: e + 1 };
      else if (c === "\n" && s[e + 1] === "\n") return null;
    }
    return null;
  }

  private skipOpts(k: number, to: number): number {
    for (let o = this.optArg(k, to); o; o = this.optArg(k, to)) k = o.next;
    return k;
  }

  /** A {group}, or a single token when there are no braces (\frac12, \textbf x). */
  private reqArg(k: number, to: number): Arg | null {
    const s = this.src;
    const j = this.skipSpaces(k, to);
    if (j >= to) return null;
    if (s[j] === "{") {
      const e = this.matchBrace(j, to);
      return { from: j + 1, to: e, next: Math.min(to, e + 1) };
    }
    if (s[j] === "}" || s[j] === "\n") return null;
    if (s[j] === "\\") {
      let e = j + 1;
      if (isLetter(s[e])) while (e < to && isLetter(s[e])) e++;
      else e++;
      return { from: j, to: e, next: e };
    }
    return { from: j, to: j + 1, next: j + 1 };
  }

  /** A brace group directly after `k` (spaces allowed), for optional brace arguments of unknown commands. */
  private braceArg(k: number, to: number): Arg | null {
    const j = this.skipSpaces(k, to);
    if (this.src[j] !== "{") return null;
    const e = this.matchBrace(j, to);
    return { from: j + 1, to: e, next: Math.min(to, e + 1) };
  }

  private argText(a: Arg): string {
    return this.src.slice(a.from, a.to);
  }

  /** The matching \end{name}: where its content ends, and just after it. */
  private findEnd(name: string, from: number, to: number): { contentEnd: number; next: number } {
    const re = /\\(begin|end)\s*\{([^}]*)\}/g;
    re.lastIndex = from;
    let depth = 0;
    for (let m = re.exec(this.src); m && m.index < to; m = re.exec(this.src)) {
      if (m[2].trim() !== name || this.inComment(m.index)) continue;
      if (m[1] === "begin") depth++;
      else if (depth-- === 0) return { contentEnd: m.index, next: m.index + m[0].length };
    }
    return { contentEnd: to, next: to };
  }

  /** Reads text that isn't in the file (a macro or glossary definition), credited to source[from, to). */
  private virtual(body: string, from: number, to: number, math: boolean) {
    if (this.depth >= 4) return;
    const r = new Reader(body, this.out, this.ctx, this.depth + 1);
    r.groupLetters = this.groupLetters;
    const outer = this.out.fixed;
    if (!outer) this.out.fixed = [from, to];
    if (math) r.mathText(0, body.length);
    else r.text(0, body.length);
    this.out.fixed = outer;
  }

  // ------------------------------------------------ text mode

  text(from: number, to: number) {
    const s = this.src;
    let i = from;
    while (i < to && !this.stopped) {
      const c = s[i];
      if (c === "%") i = this.skipComment(i, to);
      else if (c === "\\") i = this.command(i, to);
      else if (c === "$") i = this.dollar(i, to);
      else if (c === "{" || c === "}" || c === "#" || c === "^" || c === "_") i++;
      else if (c === "~" || c === "&") {
        this.out.push(" ", i, i + 1);
        i++;
      } else if (c === "\n") {
        let j = i + 1;
        while (j < to && (s[j] === " " || s[j] === "\t" || s[j] === "\r")) j++;
        if (j < to && s[j] === "\n") {
          this.out.brk();
          i = j + 1;
        } else {
          this.out.push(" ", i, i + 1);
          i++;
        }
      } else if (c === "-" && s[i + 1] === "-") {
        if (s[i + 2] === "-") {
          this.out.push(" — ", i, i + 3);
          i += 3;
        } else {
          let p = i - 1;
          while (p >= from && s[p] === " ") p--;
          this.out.push(isDigit(s[p]) && isDigit(s[i + 2]) ? " to " : " – ", i, i + 2);
          i += 2;
        }
      } else if (c === "`") {
        const two = s[i + 1] === "`";
        this.out.push(two ? "“" : "‘", i, i + (two ? 2 : 1));
        i += two ? 2 : 1;
      } else if (c === "'" && s[i + 1] === "'") {
        this.out.push("”", i, i + 2);
        i += 2;
      } else {
        // A run of ordinary characters, each mapped to itself.
        let j = i + 1;
        while (j < to && !"%\\${}#^_~&\n-`'".includes(s[j])) j++;
        if (j === i + 1 && (s[i] === "-" || s[i] === "'")) j = i + 1;
        this.out.source(s, i, j);
        i = j;
      }
    }
  }

  private dollar(i: number, to: number): number {
    const s = this.src;
    if (s[i + 1] === "$") {
      let e = s.indexOf("$$", i + 2);
      if (e < 0 || e >= to) e = to;
      this.math(i + 2, e, true);
      return Math.min(to, e + 2);
    }
    let k = i + 1;
    while (k < to && s[k] !== "$") {
      if (s[k] === "\\") k++;
      else if (s[k] === "\n" && /^[ \t\r]*\n/.test(s.slice(k + 1, k + 40))) return i + 1; // unclosed: a blank line comes first
      k++;
    }
    this.math(i + 1, Math.min(k, to), false);
    return Math.min(to, k + 1);
  }

  /** Maths, said as words. Display maths that would take too long to say becomes "equation". */
  private math(from: number, to: number, display: boolean) {
    const outer = this.out;
    const tmp = new Out();
    tmp.fixed = outer.fixed;
    this.out = tmp;
    const grouped = this.groupLetters;
    this.groupLetters = false;
    this.lastMathWord = "";
    this.mathText(from, to);
    this.groupLetters = grouped;
    this.out = outer;
    const words = tmp.words();
    if (!words) return;
    if (display && words > 40) {
      // Keep the sentence's own full stop if the maths ended it.
      const end = /[.,;]\s*(\\(end|\]).*)?$/s.exec(this.src.slice(from, to).trimEnd());
      outer.push(" equation", from, to);
      if (end) outer.punct(end[0][0], from, to);
      else outer.push(" ", from, to);
      return;
    }
    outer.push(" ", from, from);
    outer.append(tmp);
    outer.push(" ", to, to);
  }

  private word(w: string, from: number, to: number) {
    if (!w) return;
    this.out.push(` ${w} `, from, to);
    this.lastMathWord = w;
  }

  mathText(from: number, to: number) {
    const s = this.src;
    let i = from;
    while (i < to) {
      const c = s[i];
      if (c === "%") {
        const n = s.indexOf("\n", i);
        i = n < 0 || n >= to ? to : n + 1;
      } else if (c === " " || c === "\n" || c === "\t" || c === "\r" || c === "&") i++;
      else if (c === "~") i++;
      else if (c === "{") {
        const e = this.matchBrace(i, to);
        this.mathText(i + 1, e);
        i = e + 1;
      } else if (c === "}") i++;
      else if (isDigit(c) || (c === "." && isDigit(s[i + 1]))) {
        let k = i;
        while (k < to && (isDigit(s[k]) || ((s[k] === "." || s[k] === ",") && isDigit(s[k + 1]) && (s[k] === "." || /^\d{3}(?!\d)/.test(s.slice(k + 1, k + 5)))))) k++;
        this.word(s.slice(i, k).replace(/,/g, ""), i, k);
        i = this.unitAfterNumber(k, to);
      } else if (isLetter(c)) {
        if (this.groupLetters) {
          let k = i;
          while (k < to && isLetter(s[k])) k++;
          this.word(s.slice(i, k), i, k);
          i = k;
        } else {
          this.word(c, i, i + 1);
          i++;
        }
      } else if (c === "^" || c === "_") i = this.script(c, i, to);
      else if (c === "'") {
        let k = i;
        while (s[k] === "'") k++;
        this.word(k - i === 1 ? "prime" : k - i === 2 ? "double prime" : "triple prime", i, k);
        i = k;
      } else if (c === "\\") i = this.mathCommand(i, to);
      else {
        const w = MATH_OPS[c];
        if (w === "," || w === "." || w === ";") this.out.punct(w, i, i + 1);
        else if (w) this.word(w, i, i + 1);
        else if (w === undefined && c.trim()) this.word(c, i, i + 1);
        i++;
      }
    }
  }

  /** $1000~mm$: letters straight after a number that spell a unit are said as the unit. */
  private unitAfterNumber(k: number, to: number): number {
    const s = this.src;
    let j = k;
    let spaced = false;
    for (;;) {
      if (s[j] === "~" || s[j] === " ") {
        spaced = true;
        j++;
      } else if (s[j] === "\\" && (s[j + 1] === "," || s[j + 1] === ";" || s[j + 1] === " ")) {
        spaced = true;
        j += 2;
      } else break;
    }
    let e = j;
    while (e < to && isLetter(s[e])) e++;
    const sym = s.slice(j, e);
    const unit = UNIT_SYMBOLS[sym];
    if (!unit || (sym.length === 1 && !spaced) || isLetter(s[e])) return k;
    this.word(unit[1], j, e);
    return e;
  }

  /** A math argument: a {group}, one \command, or one character. */
  private mathArg(k: number, to: number): Arg | null {
    const s = this.src;
    let j = k;
    while (j < to && (s[j] === " " || s[j] === "\n" || s[j] === "\t")) j++;
    if (j >= to) return null;
    if (s[j] === "{") {
      const e = this.matchBrace(j, to);
      return { from: j + 1, to: e, next: Math.min(to, e + 1) };
    }
    if (s[j] === "\\") {
      let e = j + 1;
      if (isLetter(s[e])) while (e < to && isLetter(s[e])) e++;
      else e++;
      return { from: j, to: e, next: e };
    }
    return { from: j, to: j + 1, next: j + 1 };
  }

  private script(c: string, i: number, to: number): number {
    const a = this.mathArg(i + 1, to);
    if (!a) return i + 1;
    const inner = this.argText(a).trim();
    if (c === "^") {
      if (inner === "2") this.word("squared", i, a.next);
      else if (inner === "3") this.word("cubed", i, a.next);
      else if (inner === "\\prime" || inner === "'") this.word("prime", i, a.next);
      else if (inner === "\\circ") this.word("degrees", i, a.next);
      else {
        this.word("to the", i, a.from);
        this.mathText(a.from, a.to);
      }
    } else {
      this.word(this.lastMathWord === "log" ? "base" : "sub", i, a.from);
      const grouped = this.groupLetters;
      // Subscripts are usually labels: x_{max} is "x sub max", not "x sub m a x".
      if (/^[A-Za-z]{2,}$/.test(inner)) this.groupLetters = true;
      this.mathText(a.from, a.to);
      this.groupLetters = grouped;
    }
    return a.next;
  }

  private mathCommand(i: number, to: number): number {
    const s = this.src;
    const n = s[i + 1];
    if (!isLetter(n)) {
      if (n === "\\") {
        this.out.punct(",", i, i + 2);
        const o = this.optArg(i + 2, to);
        return o ? o.next : i + 2;
      }
      if (n === "%") this.word("percent", i, i + 2);
      if (n === "{" || n === "}") this.lastMathWord = "";
      return i + 2;
    }
    let j = i + 1;
    while (j < to && isLetter(s[j])) j++;
    const name = s.slice(i + 1, j);
    if (s[j] === "*") j++;

    if (GREEK.has(name)) {
      this.word(name, i, j);
      return j;
    }
    if (VAR_GREEK[name]) {
      this.word(VAR_GREEK[name], i, j);
      return j;
    }
    if (DELIMITED.has(name)) {
      // \left( and \right\} : the delimiter is silent too.
      let k = j;
      while (s[k] === " ") k++;
      if (s[k] === "\\") {
        k++;
        if (isLetter(s[k])) while (isLetter(s[k])) k++;
        else k++;
      } else k++;
      return Math.min(k, to);
    }
    if (MATH_WORDS[name] !== undefined) {
      this.word(MATH_WORDS[name], i, j);
      return j;
    }
    if (FUNCS.has(name)) {
      this.word(name === "ln" ? "natural log" : name === "lg" ? "log" : name, i, j);
      return j;
    }
    if (name === "ln") {
      this.word("natural log", i, j);
      return j;
    }
    if (name === "frac" || name === "dfrac" || name === "tfrac" || name === "cfrac" || name === "nicefrac" || name === "sfrac") {
      const a = this.mathArg(j, to);
      const b = a && this.mathArg(a.next, to);
      if (!a || !b) return j;
      this.mathText(a.from, a.to);
      this.word("over", a.next, b.from);
      this.mathText(b.from, b.to);
      return b.next;
    }
    if (name === "sqrt") {
      const o = this.optArg(j, to);
      const a = this.mathArg(o ? o.next : j, to);
      if (!a) return j;
      if (o) {
        this.word("the", i, j);
        this.mathText(o.from, o.to);
        this.word("th root of", o.to, a.from);
      } else this.word("the square root of", i, a.from);
      this.mathText(a.from, a.to);
      return a.next;
    }
    if (MATH_ACCENTS[name] !== undefined) {
      const a = this.mathArg(j, to);
      if (!a) return j;
      this.mathText(a.from, a.to);
      this.word(MATH_ACCENTS[name], a.to, a.next);
      return a.next;
    }
    if (name === "vec" || name === "overrightarrow") {
      const a = this.mathArg(j, to);
      if (!a) return j;
      this.word("vector", i, a.from);
      this.mathText(a.from, a.to);
      return a.next;
    }
    if (MATH_FONTS.has(name)) {
      const a = this.mathArg(j, to);
      if (!a) return j;
      const grouped = this.groupLetters;
      this.groupLetters = true;
      this.mathText(a.from, a.to);
      this.groupLetters = grouped;
      return a.next;
    }
    if (MATH_TEXT.has(name)) {
      const a = this.reqArg(j, to);
      if (!a) return j;
      this.out.push(" ", i, a.from);
      this.text(a.from, a.to);
      this.out.push(" ", a.to, a.next);
      this.lastMathWord = "";
      return a.next;
    }
    if (name === "label" || name === "tag" || name === "hspace" || name === "vspace" || name === "phantom" || name === "hphantom" || name === "vphantom" || name === "color") {
      const a = this.reqArg(this.skipOpts(j, to), to);
      return a ? a.next : j;
    }
    if (name === "begin") {
      const a = this.reqArg(j, to);
      if (!a) return j;
      const env = this.argText(a).trim();
      const end = this.findEnd(env, a.next, to);
      const base = env.replace(/\*$/, "");
      if (MATRIX_ENVS.has(base)) this.word("matrix", i, end.next);
      else {
        let k = a.next;
        if (base === "aligned" || base === "alignedat" || base === "gathered" || base === "split" || base === "cases" || base === "subarray") k = this.skipOpts(k, to);
        if (base === "alignedat" || base === "subarray") k = this.reqArg(k, to)?.next ?? k;
        if (base === "cases" || base === "dcases") this.word("cases", i, a.next);
        this.mathText(k, end.contentEnd);
      }
      return end.next;
    }
    if (name === "end") return this.reqArg(j, to)?.next ?? j;
    if (SIUNITX.has(name)) return this.siunitx(name, i, j, to);
    if (/cite/i.test(name)) return this.cite(name, i, j, to);
    if (REFS.has(name)) return this.ref(name, i, j, to);
    if (GLS_RE.test(name)) return this.gls(name, i, j, to);

    const body = this.ctx.macros?.get(name);
    if (body !== undefined) {
      this.virtual(body, i, j, true);
      return j;
    }
    // Unknown: read any arguments, and say short names (\Rw → "Rw").
    let k = this.skipOpts(j, to);
    let read = false;
    for (let a = this.braceArg(k, to); a; a = this.braceArg(k, to)) {
      this.mathText(a.from, a.to);
      k = a.next;
      read = true;
    }
    if (!read && name.length <= 4) this.word(name, i, j);
    return k;
  }

  // ------------------------------------------------ commands

  private command(i: number, to: number): number {
    const s = this.src;
    const n = s[i + 1];
    if (i + 1 >= to) return to;
    if (!isLetter(n)) return this.symbol(i, to);
    let j = i + 1;
    while (j < to && (isLetter(s[j]) || s[j] === "@")) j++;
    const name = s.slice(i + 1, j);
    if (s[j] === "*") j++;
    return this.dispatch(name, i, j, to);
  }

  private symbol(i: number, to: number): number {
    const s = this.src;
    const c = s[i + 1];
    switch (c) {
      case "\\": {
        let k = i + 2;
        if (s[k] === "*") k++;
        const o = s[this.skipSpaces(k, to)] === "[" ? this.optArg(k, to) : null;
        this.out.push(" ", i, k);
        return o ? o.next : k;
      }
      case "%":
      case "&":
      case "$":
      case "#":
      case "_":
      case "{":
      case "}":
        this.out.push(c, i, i + 2);
        return i + 2;
      case ",":
      case ";":
      case ":":
      case " ":
      case ">":
      case "\n":
        this.out.push(" ", i, i + 2);
        return i + 2;
      case "(": {
        let e = s.indexOf("\\)", i + 2);
        if (e < 0 || e >= to) e = to;
        this.math(i + 2, e, false);
        return Math.min(to, e + 2);
      }
      case "[": {
        let e = s.indexOf("\\]", i + 2);
        if (e < 0 || e >= to) e = to;
        this.math(i + 2, e, true);
        return Math.min(to, e + 2);
      }
      default:
        if (SYMBOL_ACCENTS[c]) return this.accent(SYMBOL_ACCENTS[c], i, i + 2, to);
        return i + 2;
    }
  }

  private accent(mark: string, i: number, j: number, to: number): number {
    const a = this.reqArg(j, to);
    if (!a) return j;
    let base = this.argText(a).trim();
    if (base === "\\i") base = "i";
    if (base === "\\j") base = "j";
    if (base.length !== 1) {
      this.text(a.from, a.to);
      return a.next;
    }
    this.out.push((base + mark).normalize("NFC"), i, a.next);
    return a.next;
  }

  private dispatch(name: string, i: number, j: number, to: number): number {
    const s = this.src;
    if (name === "begin") return this.env(i, j, to);
    if (name === "end") {
      const a = this.reqArg(j, to);
      if (a && this.argText(a).trim() === "document") this.stopped = true;
      return a ? a.next : j;
    }
    if (SECTIONS.has(name)) {
      const a = this.reqArg(this.skipOpts(j, to), to);
      if (!a) return j;
      this.out.brk();
      this.text(a.from, a.to);
      this.out.brk();
      return a.next;
    }
    if (name === "caption" || name === "subcaption" || name === "captionof") {
      let k = j;
      let label = this.captionLabel;
      if (name === "captionof") {
        const t = this.reqArg(k, to);
        if (!t) return j;
        const type = this.argText(t).trim();
        label = FLOATS[type] ?? null;
        k = t.next;
      }
      const a = this.reqArg(this.skipOpts(k, to), to);
      if (!a) return j;
      this.out.brk();
      if (label) this.out.push(`${label}: `, i, j);
      this.text(a.from, a.to);
      this.out.brk();
      return a.next;
    }
    if (name === "item") {
      this.out.brk();
      const o = s[this.skipSpaces(j, to)] === "[" ? this.optArg(j, to) : null;
      if (!o) return j;
      this.text(o.from, o.to);
      this.out.punct(":", o.to, o.next);
      return o.next;
    }
    if (name === "footnote") {
      const a = this.reqArg(this.skipOpts(j, to), to);
      if (!a) return j;
      this.out.push(" (footnote: ", i, a.from);
      this.text(a.from, a.to);
      this.out.push(") ", a.to, a.next);
      return a.next;
    }
    if (/cite/i.test(name)) return this.cite(name, i, j, to);
    if (REFS.has(name)) return this.ref(name, i, j, to);
    if (GLS_RE.test(name)) return this.gls(name, i, j, to);
    if (SIUNITX.has(name)) return this.siunitx(name, i, j, to);
    if (LETTER_ACCENTS[name] && (s[j] === "{" || s[j] === " ")) return this.accent(LETTER_ACCENTS[name], i, j, to);
    if (READ[name] !== undefined) {
      let k = this.skipOpts(j, to);
      for (let n = 0; n < READ[name]; n++) {
        const a = this.reqArg(k, to);
        if (!a) return k;
        k = this.skipOpts(a.next, to);
      }
      const a = this.reqArg(k, to);
      if (!a) return k;
      this.text(a.from, a.to);
      return a.next;
    }
    if (name === "ensuremath") {
      const a = this.reqArg(j, to);
      if (!a) return j;
      this.math(a.from, a.to, false);
      return a.next;
    }
    if (name === "verb" || name === "lstinline" || name === "mintinline") {
      let k = name === "mintinline" ? (this.reqArg(this.skipOpts(j, to), to)?.next ?? j) : this.skipOpts(j, to);
      const delim = s[k];
      if (!delim) return k;
      const close = delim === "{" ? "}" : delim;
      const e = s.indexOf(close, k + 1);
      if (e < 0 || e >= to) return k + 1;
      this.out.source(s, k + 1, e);
      return e + 1;
    }
    if (name === "newcommand" || name === "renewcommand" || name === "providecommand" || name === "DeclareRobustCommand" || name === "DeclareMathOperator" || name === "def" || name === "gdef" || name === "edef" || name === "let") {
      return this.skipDefinition(name, j, to);
    }
    if (SKIP[name] !== undefined) {
      let k = this.skipOpts(j, to);
      for (let n = 0; n < SKIP[name]; n++) {
        const a = this.reqArg(k, to);
        if (!a) return k;
        k = this.skipOpts(a.next, to);
      }
      return k;
    }
    if (SILENT.has(name)) {
      if (SPACERS.has(name)) this.out.push(" ", i, j);
      return j;
    }
    if (WORDS[name] !== undefined) {
      this.out.push(WORDS[name], i, j);
      return j;
    }
    const body = this.ctx.macros?.get(name);
    if (body !== undefined) {
      this.virtual(body, i, j, false);
      return j;
    }
    // Unknown: probably a formatting macro, so read its arguments, unless it's set-up like \titleformat or \rowcolors.
    const setup = CONFIG_RE.test(name);
    let k = this.skipOpts(j, to);
    for (let a = this.braceArg(k, to); a; a = this.braceArg(k, to)) {
      if (!setup) this.text(a.from, a.to);
      k = this.skipOpts(a.next, to);
    }
    return k;
  }

  private skipDefinition(name: string, j: number, to: number): number {
    const s = this.src;
    let k = this.skipSpaces(j, to);
    if (name === "let") {
      // \let\a\b or \let\a=\b
      k = this.reqArg(k, to)?.next ?? k;
      k = this.skipSpaces(k, to);
      if (s[k] === "=") k++;
      return this.reqArg(k, to)?.next ?? k;
    }
    if (name === "def" || name === "gdef" || name === "edef") {
      k = this.reqArg(k, to)?.next ?? k;
      while (k < to && s[k] !== "{") k++; // parameter text, e.g. #1#2
      return this.reqArg(k, to)?.next ?? k;
    }
    k = this.reqArg(k, to)?.next ?? k; // the name
    k = this.skipOpts(k, to); // [n][default]
    return this.reqArg(k, to)?.next ?? k; // the body
  }

  private env(i: number, j: number, to: number): number {
    const a = this.reqArg(j, to);
    if (!a) return j;
    const name = this.argText(a).trim();
    const base = name.replace(/\*$/, "");
    const end = this.findEnd(name, a.next, to);
    let k = a.next;
    if (base === "document") {
      this.text(k, end.contentEnd);
      this.stopped = true;
      return end.next;
    }
    if (MATH_ENVS.has(base)) {
      if (base === "alignat") k = this.reqArg(k, to)?.next ?? k;
      this.math(k, end.contentEnd, true);
      return end.next;
    }
    if (base === "subequations") {
      this.text(k, end.contentEnd);
      return end.next;
    }
    if (SKIP_ENVS.has(base) || SKIP_ENVS.has(name)) return end.next;
    if (FLOATS[base]) {
      this.captions(FLOATS[base], k, end.contentEnd);
      return end.next;
    }
    if (LISTS.has(base)) {
      this.out.brk();
      this.text(this.skipOpts(k, to), end.contentEnd);
      this.out.brk();
      return end.next;
    }
    if (THEOREMS[base]) {
      this.out.brk();
      this.out.push(`${THEOREMS[base]}. `, i, a.next);
      const o = this.optArg(k, to);
      if (o) {
        this.text(o.from, o.to);
        this.out.punct(".", o.to, o.next);
        k = o.next;
      }
      this.text(k, end.contentEnd);
      this.out.brk();
      return end.next;
    }
    k = this.skipOpts(k, to);
    for (let n = 0; n < (ENV_ARGS[base] ?? 0); n++) k = this.skipOpts(this.reqArg(k, to)?.next ?? k, to);
    this.out.brk();
    this.text(k, end.contentEnd);
    this.out.brk();
    return end.next;
  }

  /** A float: just its captions (and subfigures' captions), each its own sentence. */
  private captions(label: string, from: number, to: number) {
    const re = /\\(captionof|subcaption|caption)(?![a-zA-Z@])/g;
    re.lastIndex = from;
    const outer = this.captionLabel;
    this.captionLabel = label;
    for (let m = re.exec(this.src); m && m.index < to; m = re.exec(this.src)) {
      if (this.inComment(m.index)) continue;
      re.lastIndex = this.command(m.index, to);
    }
    this.captionLabel = outer;
  }

  private cite(name: string, i: number, j: number, to: number): number {
    let k = this.skipOpts(j, to);
    const keys: string[] = [];
    // \textcites{a}{b} take several groups of keys.
    for (let a = this.reqArg(k, to); a; a = name.endsWith("cites") ? this.braceArg(this.skipOpts(k, to), to) : null) {
      keys.push(...this.argText(a).split(",").map((t) => t.trim()).filter(Boolean));
      k = a.next;
      if (!name.endsWith("cites")) break;
    }
    const cites = this.ctx.citations;
    const author = (key: string) => {
      const c = cites?.get(key);
      return c?.author ? authorWords(c.author) : labelWords(key).words;
    };
    const year = (key: string) => cites?.get(key)?.year ?? "";
    let say = "";
    if (/author/i.test(name)) say = joinAnd(keys.map(author));
    else if (/year/i.test(name)) say = joinAnd(keys.map(year));
    else if (TEXTUAL_CITE.test(name)) say = joinAnd(keys.map((key) => (year(key) ? `${author(key)} (${year(key)})` : author(key))));
    // Parenthetical citations (\cite, \parencite, \citep, ...) aren't read: they'd break up every sentence.
    if (say) this.out.push(` ${say} `, i, k);
    return k;
  }

  private ref(name: string, i: number, j: number, to: number): number {
    const a = this.reqArg(this.skipOpts(j, to), to);
    if (!a) return j;
    const typed = TYPED_REFS.has(name);
    const capital = /^[A-Z]/.test(name);
    const parts = this.argText(a)
      .split(",")
      .map((key) => {
        const { type, words } = labelWords(key.trim());
        if (!typed || !type) return words;
        return `${capital ? type[0].toUpperCase() + type.slice(1) : type} ${words}`;
      });
    this.out.push(` ${joinAnd(parts)} `, i, a.next);
    return a.next;
  }

  private gls(name: string, i: number, j: number, to: number): number {
    const a = this.reqArg(this.skipOpts(j, to), to);
    if (!a) return j;
    const key = this.argText(a).trim();
    const g = this.ctx.glossary?.get(key);
    const plural = /pl$|^ac[slf]?p$/i.test(name);
    const long = /long|desc|^acl/i.test(name);
    const full = /full|^acfp?$/i.test(name);
    if (g) {
      const say = long && g.long ? g.long : full && g.long ? `${g.long} (${g.short})` : plural ? (g.plural ?? `${g.short}s`) : g.short;
      this.out.push(" ", i, i);
      this.virtual(say, i, a.next, false);
      this.out.push(" ", a.next, a.next);
    } else {
      const t = key.length <= 5 ? key.toUpperCase() : key.replace(/[_\-:.]+/g, " ");
      this.out.push(` ${t}${plural ? "s" : ""} `, i, a.next);
    }
    return a.next;
  }

  private siunitx(name: string, i: number, j: number, to: number): number {
    const args: Arg[] = [];
    const want = name === "SI" || name === "qty" || name === "numrange" ? 2 : name === "SIrange" || name === "qtyrange" ? 3 : name === "SIlist" || name === "qtylist" ? 2 : 1;
    let k = this.skipOpts(j, to);
    for (let n = 0; n < want; n++) {
      const a = this.reqArg(k, to);
      if (!a) return k;
      args.push(a);
      k = this.skipOpts(a.next, to);
    }
    const t = (a: Arg) => this.argText(a);
    let say: string;
    switch (name) {
      case "si":
      case "unit":
        say = unitWords(t(args[0]), true);
        break;
      case "num":
      case "tablenum":
        say = numberWords(t(args[0]));
        break;
      case "numlist":
        say = joinAnd(t(args[0]).split(";").map(numberWords));
        break;
      case "ang":
        say = `${numberWords(t(args[0]).split(";")[0])} degrees`;
        break;
      case "numrange":
        say = `${numberWords(t(args[0]))} to ${numberWords(t(args[1]))}`;
        break;
      case "SIrange":
      case "qtyrange":
        say = `${numberWords(t(args[0]))} to ${numberWords(t(args[1]))} ${unitWords(t(args[2]), true)}`;
        break;
      case "SIlist":
      case "qtylist":
        say = `${joinAnd(t(args[0]).split(";").map(numberWords))} ${unitWords(t(args[1]), true)}`;
        break;
      default: {
        const num = numberWords(t(args[0]));
        say = `${num} ${unitWords(t(args[1]), num.trim() !== "1")}`;
      }
    }
    this.out.push(` ${say} `, i, k);
    return k;
  }
}

const GLS_RE = /^(gls|Gls|GLS)(pl|text|name|entryname|entrytext|entryshort|entrylong|entrydesc|desc|symbol|xtrshort|xtrlong|xtrfull|first)?$|^(acr|Acr)(short|long|full)(pl)?$|^(ac|Ac|acs|acl|acf|acp|Acp|acsp|aclp|acfp)$/;

/** siunitx numbers: "1e3" → "1 times 10 to the 3". */
function numberWords(n: string): string {
  const t = n.replace(/[{}\s~]/g, "").replace(/\\pm/g, " plus or minus ").replace(/\\times|x(?=\d)/g, " times ");
  const e = /^([+-]?[\d.,]+)[eE]([+-]?\d+)$/.exec(t);
  if (e) return `${e[1]} times 10 to the ${e[2].replace(/^\+/, "").replace(/^-/, "minus ")}`;
  return t.replace(/^-/, "minus ");
}

/** siunitx units, as macros (\kilo\metre\per\second) or symbols (km/s). */
function unitWords(u: string, plural: boolean): string {
  const words: string[] = [];
  let prefix = "";
  let per = false;
  const say = (pair: [string, string] | undefined, fallback: string) => {
    const w = pair ? pair[plural && !per ? 1 : 0] : fallback;
    words.push(prefix + w);
    prefix = "";
  };
  for (const tok of u.match(/\\[a-zA-Z]+|[a-zA-Z]+|\/|\^-?\d|%|\S/g) ?? []) {
    if (tok.startsWith("\\")) {
      const name = tok.slice(1);
      if (PREFIXES[name]) prefix += PREFIXES[name];
      else if (name === "per") {
        words.push("per");
        per = true;
      } else if (name === "squared" || name === "cubed") words.push(name);
      else if (name === "square" || name === "cubic") words.push(name);
      else if (UNITS[name]) say(UNITS[name], name);
      else say(undefined, name);
    } else if (tok === "/") {
      words.push("per");
      per = true;
    } else if (tok === "^2") words.push("squared");
    else if (tok === "^3") words.push("cubed");
    else if (tok.startsWith("^")) words.push(`to the ${tok.slice(1)}`);
    else if (tok === "%") words.push("percent");
    else if (isLetter(tok[0])) say(UNIT_SYMBOLS[tok], tok);
  }
  return words.join(" ");
}

// ---------------------------------------------------------------- sentences

const MAX_SENTENCE = 260;

function toSentences(out: Out): Sentence[] {
  const sentences: Sentence[] = [];
  const segmenter = new Intl.Segmenter("en", { granularity: "sentence" });
  const add = (b: Block, text: string, a: number, e: number) => {
    while (a < e && text[a] === " ") a++;
    while (e > a && text[e - 1] === " ") e--;
    if (!/[\p{L}\p{N}]/u.test(text.slice(a, e))) return;
    // Long sentences split at a comma or semicolon, so highlighting keeps up and no utterance runs on.
    while (e - a > MAX_SENTENCE) {
      const head = text.slice(a, a + MAX_SENTENCE);
      const cut = Math.max(head.lastIndexOf("; "), head.lastIndexOf(", "), head.lastIndexOf(": "));
      if (cut < 60) break;
      add(b, text, a, a + cut + 1);
      a += cut + 2;
    }
    const starts = Int32Array.from(b.starts.slice(a, e));
    const ends = Int32Array.from(b.ends.slice(a, e));
    let from = Infinity;
    let to = -Infinity;
    for (let k = 0; k < starts.length; k++) {
      if (starts[k] < from) from = starts[k];
      if (ends[k] > to) to = ends[k];
    }
    sentences.push({ text: text.slice(a, e), from, to, starts, ends });
  };
  for (const b of out.blocks) {
    const text = b.chars.join("");
    for (const { segment, index } of segmenter.segment(text)) add(b, text, index, index + segment.length);
  }
  return sentences;
}

/** Where the document body starts, or 0 for a file that's \input into one. */
function bodyStart(src: string): number {
  const re = /\\begin\s*\{document\}/g;
  for (let m = re.exec(src); m; m = re.exec(src)) {
    const lineStart = src.lastIndexOf("\n", m.index - 1) + 1;
    if (!/(^|[^\\])%/.test(src.slice(lineStart, m.index))) return m.index;
  }
  return 0;
}

export function latexToSpeech(src: string, ctx: SpeechContext = {}): Sentence[] {
  const out = new Out();
  new Reader(src, out, ctx).text(bodyStart(src), src.length);
  out.brk();
  return toSentences(out);
}

/** Plain text and Markdown: paragraphs split on blank lines, with Markdown's markup left unsaid. */
export function plainToSpeech(src: string): Sentence[] {
  const out = new Out();
  let lineStart = true;
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (c === "\n") {
      if (/^[ \t\r]*\n/.test(src.slice(i + 1, i + 40)) || /^\s{0,3}(#|[-*+] |\d+\. |>)/.test(src.slice(i + 1, i + 8))) out.brk();
      else out.push(" ", i, i + 1);
      lineStart = true;
      continue;
    }
    if (lineStart && (c === "#" || c === ">")) continue;
    if (c === "*" || c === "_" || c === "`") continue;
    lineStart = lineStart && (c === " " || c === "\t");
    out.source(src, i, i + 1);
  }
  out.brk();
  return toSentences(out);
}

// ---------------------------------------------------------------- definitions

/** The content of the {group} at or after `k` (spaces allowed), and where it ends. */
function group(s: string, k: number): [string, number] | null {
  while (s[k] === " " || s[k] === "\n" || s[k] === "\t" || s[k] === "\r") k++;
  if (s[k] !== "{") return null;
  let depth = 0;
  for (let e = k; e < s.length; e++) {
    if (s[e] === "\\") e++;
    else if (s[e] === "{") depth++;
    else if (s[e] === "}" && --depth === 0) return [s.slice(k + 1, e), e + 1];
  }
  return null;
}

/** "name={STL}, description={Sound ...}" → a map, splitting on top-level commas. */
function keyvals(body: string): Map<string, string> {
  const map = new Map<string, string>();
  let depth = 0;
  let start = 0;
  const take = (part: string) => {
    const eq = part.indexOf("=");
    if (eq < 0) return;
    let v = part.slice(eq + 1).trim();
    if (v.startsWith("{") && v.endsWith("}")) v = v.slice(1, -1);
    map.set(part.slice(0, eq).trim().toLowerCase(), v.trim());
  };
  for (let k = 0; k < body.length; k++) {
    const c = body[k];
    if (c === "\\") k++;
    else if (c === "{") depth++;
    else if (c === "}") depth--;
    else if (c === "," && depth === 0) {
      take(body.slice(start, k));
      start = k + 1;
    }
  }
  take(body.slice(start));
  return map;
}

/** Acronyms, glossary entries and argument-less macros defined in a file. */
export function collectDefinitions(src: string, into: Required<Pick<SpeechContext, "glossary" | "macros">> = { glossary: new Map(), macros: new Map() }) {
  const s = src.replace(/(^|[^\\])%.*$/gm, "$1");
  const { glossary, macros } = into;

  for (const m of s.matchAll(/\\(?:newacronym|newabbreviation)\s*(?:\[[^\]]*\])?\s*(?=\{)/g)) {
    const key = group(s, m.index + m[0].length);
    const short = key && group(s, key[1]);
    const long = short && group(s, short[1]);
    if (key && short) glossary.set(key[0].trim(), { short: short[0], long: long?.[0] });
  }
  for (const m of s.matchAll(/\\newglossaryentry\s*(?=\{)/g)) {
    const key = group(s, m.index + m[0].length);
    const body = key && group(s, key[1]);
    if (!key || !body) continue;
    const kv = keyvals(body[0]);
    const short = kv.get("text") ?? kv.get("name");
    if (short) glossary.set(key[0].trim(), { short, long: kv.get("description") ?? kv.get("first"), plural: kv.get("plural") });
  }
  for (const m of s.matchAll(/\\DeclareAcronym\s*(?=\{)/g)) {
    const key = group(s, m.index + m[0].length);
    const body = key && group(s, key[1]);
    if (!key || !body) continue;
    const kv = keyvals(body[0]);
    const short = kv.get("short");
    if (short) glossary.set(key[0].trim(), { short, long: kv.get("long"), plural: kv.get("short-plural-form") });
  }
  for (const m of s.matchAll(/\\(?:(?:re)?newcommand|providecommand|DeclareRobustCommand)\*?\s*(?:\{\s*\\([a-zA-Z@]+)\s*\}|\\([a-zA-Z@]+))\s*(\[\s*(\d)\s*\])?/g)) {
    if (m[4] && m[4] !== "0") continue;
    const body = group(s, m.index + m[0].length);
    if (body && body[0].length <= 300 && !body[0].includes("#")) macros.set(m[1] ?? m[2], body[0]);
  }
  for (const m of s.matchAll(/\\[gex]?def\s*\\([a-zA-Z@]+)\s*(?=\{)/g)) {
    const body = group(s, m.index + m[0].length);
    if (body && body[0].length <= 300 && !body[0].includes("#")) macros.set(m[1], body[0]);
  }
  for (const m of s.matchAll(/\\DeclareMathOperator\*?\s*(?:\{\s*\\([a-zA-Z@]+)\s*\}|\\([a-zA-Z@]+))/g)) {
    const body = group(s, m.index + m[0].length);
    if (body) macros.set(m[1] ?? m[2], `\\operatorname{${body[0]}}`);
  }
  return into;
}
